import React from 'react';
import Checkbox, { TCheckBoxLabel } from '@/components/Checkbox';
import { TWalletData } from '../types/WalletData';
import AvatarIcon from '@/components/AvatarIcon';
import { TTransactionFilter } from '../data/transactionTypes';
import { getTransactionIcon } from '../data/TransactionTypeIcons';
import { generateTransactionTypeId } from '../utils/generateTransactionId';
import Button from '@/components/Button';

type FilterSectionProps<T> = {
  options: T;
  checkedOptions: (string | number)[];
  onCheckboxClick: (option: TCheckBoxLabel, isChecked: boolean) => void;
  onSelectAllToggle: (isAllSelected: boolean) => void;
  onApply: () => void;
  onReset: () => void;
};

const FilterSection: React.FC<{
  title: string;
  onCheckboxClick: (option: TCheckBoxLabel, isChecked: boolean) => void;
  checkedOptions: (string | number)[];
  options: TCheckBoxLabel[];
  onSelectAllToggle: (isSelectedAll: boolean) => void;
  onApply: () => void;
  onReset: () => void;
}> = ({ title, checkedOptions, options, onSelectAllToggle, onCheckboxClick, onApply, onReset }) => {
  const isAllSelected = options.length > 0 && checkedOptions.length === options.length;

  return (
    <>
      {/* Title + Select all on the same row */}
      <div className="flex justify-between items-center py-3 pr-3">
        <span className="text-xs font-medium text-emerge-navy px-4">{title}</span>
        <a
          onClick={() => onSelectAllToggle(isAllSelected)}
          className="text-xs text-emerge-navy-2 cursor-pointer">
          {isAllSelected ? 'Unselect all' : 'Select all'}
        </a>
      </div>

      {/* Scrollable list — Checkbox internal select-all hidden since it lives in the header */}
      <div className="max-h-72 h-auto overflow-y-auto vertical-scroll pr-3">
        <Checkbox
          hideSelectAll
          onSelectAllToggle={onSelectAllToggle}
          options={options}
          onCheckboxClick={onCheckboxClick}
          checkedOptions={checkedOptions}
        />
      </div>

      {/* Apply + Reset buttons */}
      <div className="flex gap-3 pt-4">
        <Button className="flex-1" onClick={onApply}>
          Apply
        </Button>
        <Button className="flex-1" variant="secondary" onClick={onReset}>
          Reset
        </Button>
      </div>
    </>
  );
};

export const AccountsSection: React.FC<FilterSectionProps<TWalletData[]>> = ({
  options,
  checkedOptions,
  onSelectAllToggle,
  onCheckboxClick,
  onApply,
  onReset
}) => (
  <FilterSection
    title="Accounts"
    options={options.map((account) => ({
      label: account.name,
      labelDescription: account.accountNumber,
      value: account.id
    }))}
    checkedOptions={checkedOptions}
    onSelectAllToggle={onSelectAllToggle}
    onCheckboxClick={onCheckboxClick}
    onApply={onApply}
    onReset={onReset}
  />
);

type TTeamMember = { avatar?: string; name: string; id: string };
export const TeamMembersSection: React.FC<FilterSectionProps<TTeamMember[]>> = ({
  options,
  checkedOptions,
  onSelectAllToggle,
  onCheckboxClick,
  onApply,
  onReset
}) => (
  <FilterSection
    title="Team members"
    options={options.map((member) => ({
      icon: <AvatarIcon key={`checkbox-tm-${member.id}`} name={member.name} />,
      label: member.name,
      value: member.id
    }))}
    checkedOptions={checkedOptions}
    onSelectAllToggle={onSelectAllToggle}
    onCheckboxClick={onCheckboxClick}
    onApply={onApply}
    onReset={onReset}
  />
);

export const TransactionTypesSection: React.FC<FilterSectionProps<TTransactionFilter[]>> = ({
  options,
  checkedOptions,
  onSelectAllToggle,
  onCheckboxClick,
  onApply,
  onReset
}) => (
  <FilterSection
    title="Transaction types"
    options={options.map((data: TTransactionFilter) => ({
      icon: getTransactionIcon({
        transactionType: data.transactionType,
        transactionDataType: data.type
      }),
      label: data.displayName,
      value: generateTransactionTypeId(data)
    }))}
    checkedOptions={checkedOptions}
    onSelectAllToggle={onSelectAllToggle}
    onCheckboxClick={onCheckboxClick}
    onApply={onApply}
    onReset={onReset}
  />
);
